import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Compare mode — "what changed since last fortnight" laid over the pages.
 *
 * In the URL like the fortnight selection (`?compare=1`), for the same reason:
 * the meeting's most-asked question is "what moved", and the answer has to be
 * a link someone can send. It also means the browser's back button turns it
 * off, which is the behaviour a presenter expects from something they toggled.
 *
 * **Off by default, always.** The pages state where the programme *is*; the
 * deltas state how it got there. Leading with the delta is how a mission
 * sitting at 42% for good reasons reads as a failure because it did not move
 * this fortnight.
 */

export const COMPARE_PARAM = 'compare';

export interface CompareMode {
  readonly isOn: boolean;
  readonly toggle: () => void;
  readonly set: (on: boolean) => void;
}

export function useCompareMode(): CompareMode {
  const [params, setParams] = useSearchParams();
  const isOn = params.get(COMPARE_PARAM) === '1';

  const set = useCallback(
    (on: boolean) => {
      const next = new URLSearchParams(params);
      if (on) next.set(COMPARE_PARAM, '1');
      else next.delete(COMPARE_PARAM);
      // `replace` — toggling a lens is not navigation, and leaving a history
      // entry per toggle makes the back button walk through states of the same
      // page instead of returning to the previous one.
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  return useMemo(() => ({ isOn, toggle: () => set(!isOn), set }), [isOn, set]);
}
